import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DatabaseTable } from '../pages/ControlRoomApp';
import { organizationsService, Organization, userOrganisationsService, UserOrganisation, usersService, User, apiKeysService, ApiKey, tournamentAccessService, TournamentAccess, appAccessService, AppAccess, Tournament, Season, Team, Player, TeamAccess, PlayerAccess, SeasonAccess, StatType } from '../services';
import Accordion from './shared/Accordion';
import Tooltip from './shared/Tooltip';
import TableScrollTopBar from './shared/TableScrollTopBar';
import DualCalendarDropdown from '../../../shared/components/ui/DualCalendarDropdown';
import LoadingSpinner from '../../../shared/components/ui/LoadingSpinner';
import OrganizationsTable from './tables/OrganizationsTable';
import UsersTable from './tables/UsersTable';
import UserOrganisationsTable from './tables/UserOrganisationsTable';
import ApiKeysTable from './tables/ApiKeysTable';
import TournamentAccessTable from './tables/TournamentAccessTable';
import AppAccessTable from './tables/AppAccessTable';
import TournamentsTable from './tables/TournamentsTable';
import SeasonsTable from './tables/SeasonsTable';
import TeamsTable from './tables/TeamsTable';
import PlayersTable from './tables/PlayersTable';
import TeamAccessTable from './tables/TeamAccessTable';
import PlayerAccessTable from './tables/PlayerAccessTable';
import SeasonAccessTable from './tables/SeasonAccessTable';
import StatTypesTable from './tables/StatTypesTable';

interface TableViewProps {
  table: DatabaseTable;
  searchQuery: string;
  refreshTrigger?: number;
  onEdit?: (entity: Organization | UserOrganisation | User | ApiKey | TournamentAccess | AppAccess | Tournament | Season | Team | Player | TeamAccess | PlayerAccess | SeasonAccess | StatType) => void;
}

interface TableColumn {
  name: string;
  type: string;
  nullable: boolean;
  primaryKey?: boolean;
  foreignKey?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
}

interface TableRow {
  [key: string]: string | number | boolean | null;
}

interface PaginationState {
  currentPage: number;
  totalPages: number;
  recordsPerPage: number;
  totalRecords: number;
}

interface FilterState {
  active: 'all' | 'true' | 'false';
  require_api_whitelist: 'all' | 'true' | 'false'; // Only for organisations
  role: 'all' | string; // For user_organisations and users
  global_role: 'all' | string; // Only for users
  rate_limit_per_hour: 'all' | 'low' | 'medium' | 'high'; // Only for api_keys
  tournament_access_type: 'all' | string; // Only for tournament_access
  app_name: 'all' | string; // Only for app_access
  access_level: 'all' | string; // Only for app_access
  createdStartDate: Date | null;
  createdEndDate: Date | null;
  updatedStartDate: Date | null;
  updatedEndDate: Date | null;
  expiresStartDate: Date | null;
  expiresEndDate: Date | null;
}

const TableView: React.FC<TableViewProps> = ({ table, searchQuery, refreshTrigger, onEdit }) => {
  const [data, setData] = useState<Organization[] | UserOrganisation[] | User[] | ApiKey[] | TournamentAccess[] | AppAccess[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 1,
    totalPages: 1,
    recordsPerPage: 100,
    totalRecords: 0
  });
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState<FilterState>({
    active: 'all',
    require_api_whitelist: 'all',
    role: 'all',
    global_role: 'all',
    rate_limit_per_hour: 'all',
    tournament_access_type: 'all',
    app_name: 'all',
    access_level: 'all',
    createdStartDate: null,
    createdEndDate: null,
    updatedStartDate: null,
    updatedEndDate: null,
    expiresStartDate: null,
    expiresEndDate: null
  });
  const [activeFilterType, setActiveFilterType] = useState<'created' | 'updated' | 'expires' | null>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarButtonRef = useRef<HTMLButtonElement>(null);
  const updatedCalendarButtonRef = useRef<HTMLButtonElement>(null);
  const expiresCalendarButtonRef = useRef<HTMLButtonElement>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
    if (table.name === 'organisations') {
      try {
        const organizations = await organizationsService.getAll();
        setData(organizations);
      } catch (err) {
        console.error('Error fetching organizations:', err);
      }
    } else if (table.name === 'users') {
      try {
        console.log('🔍 Fetching users in TableView...');
        const users = await usersService.getAll();
        console.log('✅ Users fetched in TableView:', users);
        setData(users);
      } catch (err) {
        console.error('❌ Error fetching users in TableView:', err);
        setData([]);
      }
    } else if (table.name === 'user_organisations') {
      try {
        const userOrganisations = await userOrganisationsService.getAll();
        setData(userOrganisations);
      } catch (err) {
        console.error('Error fetching user organisations:', err);
      }
    } else if (table.name === 'api_keys') {
      try {
        const apiKeys = await apiKeysService.getAll();
        setData(apiKeys);
      } catch (err) {
        console.error('Error fetching API keys:', err);
      }
    } else if (table.name === 'tournament_access') {
      try {
        const tournamentAccess = await tournamentAccessService.getAll();
        setData(tournamentAccess);
      } catch (err) {
        console.error('Error fetching tournament access:', err);
      }
    } else if (table.name === 'app_access') {
      try {
        const appAccess = await appAccessService.getAll();
        setData(appAccess);
      } catch (err) {
        console.error('Error fetching app access:', err);
      }
    }
    } finally {
      setIsLoading(false);
    }
  }, [table.name]);

  // Fetch data when component mounts or table changes
  useEffect(() => {
    if (table.name === 'organisations' || table.name === 'users' || table.name === 'user_organisations' || table.name === 'api_keys' || table.name === 'tournament_access' || table.name === 'app_access') {
      fetchData();
    }
  }, [fetchData, table.name]);

  // Refetch data when refreshTrigger changes (e.g., after adding new data)
  useEffect(() => {
    if ((table.name === 'organisations' || table.name === 'users' || table.name === 'user_organisations' || table.name === 'api_keys' || table.name === 'tournament_access' || table.name === 'app_access') && refreshTrigger !== undefined) {
      fetchData();
    }
  }, [fetchData, table.name, refreshTrigger]);

  // Update pagination when data changes
  useEffect(() => {
    if ((table.name === 'organisations' || table.name === 'users' || table.name === 'user_organisations' || table.name === 'api_keys' || table.name === 'tournament_access' || table.name === 'app_access') && data.length > 0) {
      const totalPages = Math.ceil(data.length / pagination.recordsPerPage);
      setPagination(prev => ({
        ...prev,
        totalPages,
        totalRecords: data.length,
        currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
      }));
    }
  }, [data, table.name, pagination.recordsPerPage]);

  // Calendar handlers
  const openCalendar = (filterType: 'created' | 'updated' | 'expires') => {
    setActiveFilterType(filterType);
    setIsCalendarOpen(true);
  };

  const handleDateRangeSelect = (startDate: Date, endDate: Date) => {
    if (activeFilterType === 'created') {
      setFilters(prev => ({
        ...prev,
        createdStartDate: startDate,
        createdEndDate: endDate
      }));
    } else if (activeFilterType === 'updated') {
      setFilters(prev => ({
        ...prev,
        updatedStartDate: startDate,
        updatedEndDate: endDate
      }));
    } else if (activeFilterType === 'expires') {
      setFilters(prev => ({
        ...prev,
        expiresStartDate: startDate,
        expiresEndDate: endDate
      }));
    }
  };

  const clearDateFilter = () => {
    if (activeFilterType === 'created') {
      setFilters(prev => ({
        ...prev,
        createdStartDate: null,
        createdEndDate: null
      }));
    } else if (activeFilterType === 'updated') {
      setFilters(prev => ({
        ...prev,
        updatedStartDate: null,
        updatedEndDate: null
      }));
    } else if (activeFilterType === 'expires') {
      setFilters(prev => ({
        ...prev,
        expiresStartDate: null,
        expiresEndDate: null
      }));
    }
  };

  // Filter applied data - separate functions for each table type
  const filterOrganizations = useCallback((orgs: Organization[]): Organization[] => {
    return orgs.filter(org => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchText = [
          org.id,
          org.name,
          org.subdomain,
          org.logo,
          ...org.allowed_domains,
          ...org.allowed_ips
        ].join(' ').toLowerCase();
        
        if (!searchText.includes(searchLower)) return false;
      }

      // Boolean filters
      if (filters.active !== 'all' && String(org.active) !== filters.active) return false;
      if (filters.require_api_whitelist !== 'all' && String(org.require_api_whitelist) !== filters.require_api_whitelist) return false;

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(org.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }
      
      if (filters.updatedStartDate && filters.updatedEndDate) {
        const updatedDate = new Date(org.updated_at);
        if (updatedDate < filters.updatedStartDate || updatedDate > filters.updatedEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  const filterUserOrganisations = useCallback((userOrgs: UserOrganisation[]): UserOrganisation[] => {
    return userOrgs.filter(userOrg => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchText = [
          userOrg.id,
          userOrg.user_id,
          userOrg.organisation_id,
          userOrg.role
        ].join(' ').toLowerCase();
        
        if (!searchText.includes(searchLower)) return false;
      }

      // Boolean filters
      if (filters.active !== 'all' && String(userOrg.active) !== filters.active) return false;
      if (filters.role !== 'all' && userOrg.role !== filters.role) return false;

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(userOrg.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }
      
      if (filters.updatedStartDate && filters.updatedEndDate) {
        const updatedDate = new Date(userOrg.updated_at);
        if (updatedDate < filters.updatedStartDate || updatedDate > filters.updatedEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  const filterUsers = useCallback((users: User[]): User[] => {
    return users.filter(user => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchText = [
          user.id,
          user.email,
          user.first_name,
          user.last_name,
          user.role,
          user.global_role
        ].join(' ').toLowerCase();
        
        if (!searchText.includes(searchLower)) return false;
      }

      // Boolean filters
      if (filters.active !== 'all' && String(user.active) !== filters.active) return false;
      if (filters.role !== 'all' && user.role !== filters.role) return false;
      if (filters.global_role !== 'all' && user.global_role !== filters.global_role) return false;

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(user.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }
      
      if (filters.updatedStartDate && filters.updatedEndDate) {
        const updatedDate = new Date(user.updated_at);
        if (updatedDate < filters.updatedStartDate || updatedDate > filters.updatedEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  // Filter function for API Keys
  const filterApiKeys = useCallback((keys: ApiKey[]): ApiKey[] => {
    return keys.filter(key => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchText = [
          key.id,
          key.key_name,
          key.api_key,
          key.organisation_id
        ].join(' ').toLowerCase();
        if (!searchText.includes(searchLower)) return false;
      }

      // Active filter
      if (filters.active !== 'all') {
        const isActive = filters.active === 'true';
        if (key.is_active !== isActive) return false;
      }


      // Rate limit filter
      if (filters.rate_limit_per_hour !== 'all') {
        const limit = key.rate_limit_per_hour;
        let matches = false;
        switch (filters.rate_limit_per_hour) {
          case 'low':
            matches = limit < 100;
            break;
          case 'medium':
            matches = limit >= 100 && limit < 1000;
            break;
          case 'high':
            matches = limit >= 1000;
            break;
        }
        if (!matches) return false;
      }

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(key.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }

      if (filters.expiresStartDate && filters.expiresEndDate && key.expires_at) {
        const expiresDate = new Date(key.expires_at);
        if (expiresDate < filters.expiresStartDate || expiresDate > filters.expiresEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  // Filter function for Tournament Access
  const filterTournamentAccess = useCallback((access: TournamentAccess[]): TournamentAccess[] => {
    return access.filter(item => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchText = [
          item.id,
          item.tournament_id,
          item.organisation_id,
          item.access_type,
          item.granted_by || ''
        ].join(' ').toLowerCase();
        if (!searchText.includes(searchLower)) return false;
      }

      // Active filter
      if (filters.active !== 'all') {
        const isActive = filters.active === 'true';
        if (item.is_active !== isActive) return false;
      }

      // Tournament Access Type filter
      if (filters.tournament_access_type !== 'all' && item.access_type !== filters.tournament_access_type) return false;

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(item.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }

      if (filters.updatedStartDate && filters.updatedEndDate) {
        const updatedDate = new Date(item.updated_at);
        if (updatedDate < filters.updatedStartDate || updatedDate > filters.updatedEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  const filterAppAccess = useCallback((data: AppAccess[]) => {
    return data.filter(item => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const searchableText = [
          item.id,
          item.organisation_id,
          item.app_name,
          item.access_level,
          item.granted_by || 'N/A'
        ].join(' ').toLowerCase();
        if (!searchableText.includes(searchLower)) return false;
      }

      // Active filter
      if (filters.active !== 'all') {
        const isActive = filters.active === 'true';
        if (item.is_active !== isActive) return false;
      }

      // App Name filter
      if (filters.app_name !== 'all' && item.app_name !== filters.app_name) return false;

      // Access Level filter
      if (filters.access_level !== 'all' && item.access_level !== filters.access_level) return false;

      // Date filters
      if (filters.createdStartDate && filters.createdEndDate) {
        const createdDate = new Date(item.created_at);
        if (createdDate < filters.createdStartDate || createdDate > filters.createdEndDate) return false;
      }

      if (filters.updatedStartDate && filters.updatedEndDate) {
        const updatedDate = new Date(item.updated_at);
        if (updatedDate < filters.updatedStartDate || updatedDate > filters.updatedEndDate) return false;
      }

      return true;
    });
  }, [searchQuery, filters]);

  // Table schema with proper min/max widths and constraints
  const getTableSchema = (tableId: string): TableColumn[] => {
    const schemas: Record<string, TableColumn[]> = {
      organisations: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'subdomain', type: 'varchar', nullable: false, width: 120, minWidth: 80, maxWidth: 150 },
        { name: 'logo', type: 'text', nullable: true, width: 100, minWidth: 60, maxWidth: 150 },
        { name: 'active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'allowed_domains', type: 'jsonb', nullable: true, width: 130, minWidth: 100, maxWidth: 180 },
        { name: 'allowed_ips', type: 'jsonb', nullable: true, width: 100, minWidth: 80, maxWidth: 130 },
        { name: 'require_api_whitelist', type: 'boolean', nullable: false, width: 130, minWidth: 100, maxWidth: 170 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
      ],
      users: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'email', type: 'varchar', nullable: false, width: 200, minWidth: 150, maxWidth: 250 },
        { name: 'first_name', type: 'varchar', nullable: false, width: 120, minWidth: 80, maxWidth: 150 },
        { name: 'last_name', type: 'varchar', nullable: false, width: 120, minWidth: 80, maxWidth: 150 },
        { name: 'role', type: 'varchar', nullable: false, width: 100, minWidth: 80, maxWidth: 120 },
        { name: 'active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'global_role', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
      ],
      user_organisations: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'user_id', type: 'uuid', nullable: false, foreignKey: 'users.id', width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'organisation_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'role', type: 'varchar', nullable: false, width: 100, minWidth: 80, maxWidth: 120 },
        { name: 'active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
      ],
      api_keys: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'key_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'api_key', type: 'varchar', nullable: false, width: 180, minWidth: 150, maxWidth: 220 },
        { name: 'organisation_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'access_type', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'rate_limit_per_hour', type: 'integer', nullable: false, width: 130, minWidth: 100, maxWidth: 160 },
        { name: 'is_active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'expires_at', type: 'timestamp', nullable: true, width: 120, minWidth: 100, maxWidth: 150 }
      ],
      tournament_access: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'tournament_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'organisation_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'access_type', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'granted_by', type: 'uuid', nullable: true, foreignKey: 'users.id', width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'granted_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'is_active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
      ],
      app_access: [
        { name: 'id', type: 'uuid', nullable: false, primaryKey: true, width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'organisation_name', type: 'varchar', nullable: false, width: 150, minWidth: 100, maxWidth: 200 },
        { name: 'app_name', type: 'varchar', nullable: false, width: 120, minWidth: 80, maxWidth: 150 },
        { name: 'access_level', type: 'varchar', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'granted_by', type: 'uuid', nullable: true, foreignKey: 'users.id', width: 250, minWidth: 200, maxWidth: 300 },
        { name: 'granted_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'is_active', type: 'boolean', nullable: false, width: 80, minWidth: 60, maxWidth: 100 },
        { name: 'created_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 },
        { name: 'updated_at', type: 'timestamp', nullable: false, width: 120, minWidth: 100, maxWidth: 150 }
      ]
    };
    return schemas[tableId] || [];
  };

  // Format table data for display
  const formatTableData = (data: Organization[] | UserOrganisation[] | User[] | ApiKey[] | TournamentAccess[] | AppAccess[]): TableRow[] => {
    if (table.name === 'organisations') {
      const orgs = data as Organization[];
      return orgs.map(org => ({
        id: org.id,
        name: org.name,
        subdomain: org.subdomain,
        logo: org.logo || '-',
        active: org.active ? 'Yes' : 'No',
        allowed_domains: (org.allowed_domains && org.allowed_domains.length > 0) ? org.allowed_domains.join(', ') : '-',
        allowed_ips: (org.allowed_ips && org.allowed_ips.length > 0) ? org.allowed_ips.join(', ') : '-',
        require_api_whitelist: org.require_api_whitelist ? 'Yes' : 'No',
        created_at: new Date(org.created_at).toLocaleDateString(),
        updated_at: new Date(org.updated_at).toLocaleDateString()
      }));
    } else if (table.name === 'users') {
      const users = data as User[];
      return users.map(user => ({
        id: user.id,
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        role: user.role,
        active: user.active ? 'Yes' : 'No',
        global_role: user.global_role,
        created_at: new Date(user.created_at).toLocaleDateString(),
        updated_at: new Date(user.updated_at).toLocaleDateString()
      }));
    } else if (table.name === 'user_organisations') {
      const userOrgs = data as UserOrganisation[];
      return userOrgs.map(userOrg => ({
        id: userOrg.id,
        user_id: userOrg.user_id, // Show UUID instead of email due to RLS issues
        organisation_name: userOrg.organisation_name || userOrg.organisation_id, // Show name if available, fallback to ID
        role: userOrg.role,
        active: userOrg.active ? 'Yes' : 'No',
        created_at: new Date(userOrg.created_at).toLocaleDateString(),
        updated_at: new Date(userOrg.updated_at).toLocaleDateString()
      }));
    } else if (table.name === 'api_keys') {
      const keys = data as ApiKey[];
      return keys.map(key => ({
        id: key.id,
        key_name: key.key_name,
        api_key: key.api_key,
        organisation_name: key.organisation_name || key.organisation_id, // Show name if available, fallback to ID
        access_type: key.access_type,
        rate_limit_per_hour: key.rate_limit_per_hour?.toString() || '0',
        is_active: key.is_active ? 'Yes' : 'No',
        created_at: new Date(key.created_at).toLocaleDateString(),
        expires_at: key.expires_at ? new Date(key.expires_at).toLocaleDateString() : 'Never'
      }));
    } else if (table.name === 'tournament_access') {
      const access = data as TournamentAccess[];
      return access.map(item => ({
        id: item.id,
        tournament_name: item.tournament_name || item.tournament_id, // Show name if available, fallback to ID
        organisation_name: item.organisation_name || item.organisation_id, // Show name if available, fallback to ID
        access_type: item.access_type,
        granted_by: item.granted_by || 'N/A',
        granted_at: new Date(item.granted_at).toLocaleDateString(),
        is_active: item.is_active ? 'Yes' : 'No',
        created_at: new Date(item.created_at).toLocaleDateString(),
        updated_at: new Date(item.updated_at).toLocaleDateString()
      }));
    } else if (table.name === 'app_access') {
      const appAccess = data as unknown as AppAccess[];
      return appAccess.map(item => ({
        id: item.id,
        organisation_name: item.organisation_name || item.organisation_id, // Show name if available, fallback to ID
        app_name: item.app_name,
        access_level: item.access_level,
        granted_by: item.granted_by || 'N/A',
        granted_at: new Date(item.granted_at).toLocaleDateString(),
        is_active: item.is_active ? 'Yes' : 'No',
        created_at: new Date(item.created_at).toLocaleDateString(),
        updated_at: new Date(item.updated_at).toLocaleDateString()
      }));
    }
    return [];
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Removed external records-per-page selector; each table handles its own

  // Sorting functionality
  const handleSort = (columnKey: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.key === columnKey) {
        return {
          key: columnKey,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
    });
  };

  const sortData = (data: TableRow[], config: { key: string | null; direction: 'asc' | 'desc' }) => {
    if (!config.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[config.key!];
      const bVal = b[config.key!];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      // Check if the values are dates in DD/MM/YYYY format
      const isDate = (val: any) => {
        if (typeof val !== 'string') return false;
        return /^\d{2}\/\d{2}\/\d{4}$/.test(val);
      };

      let comparison: number;

      if (isDate(aVal) && isDate(bVal)) {
        // Convert DD/MM/YYYY to Date objects for proper chronological sorting
        const parseDate = (dateStr: string) => {
          const [day, month, year] = dateStr.split('/');
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        };
        
        const aDate = parseDate(String(aVal));
        const bDate = parseDate(String(bVal));
        comparison = aDate.getTime() - bDate.getTime();
      } else {
        // Use string comparison for non-date values
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return config.direction === 'asc' ? comparison : -comparison;
    });
  };

  // Calculate filtered data using useMemo to prevent infinite re-renders
  const filteredData = useMemo(() => {
    if (table.name === 'organisations') {
      return filterOrganizations(data as Organization[]);
    } else if (table.name === 'users') {
      return filterUsers(data as User[]);
    } else if (table.name === 'user_organisations') {
      return filterUserOrganisations(data as UserOrganisation[]);
    } else if (table.name === 'api_keys') {
      return filterApiKeys(data as ApiKey[]);
    } else if (table.name === 'tournament_access') {
      return filterTournamentAccess(data as TournamentAccess[]);
    } else if (table.name === 'app_access') {
      return filterAppAccess(data as unknown as AppAccess[]);
    }
    return [];
  }, [data, searchQuery, filters, table.name, filterOrganizations, filterUserOrganisations, filterUsers, filterApiKeys, filterTournamentAccess, filterAppAccess]);
  
  // Calculate schema and table data
  const columns = getTableSchema(table.id);
  
  // Calculate formatted and sorted data using useMemo
  const tableData = useMemo(() => {
    if (table.name === 'organisations' || table.name === 'users' || table.name === 'user_organisations' || table.name === 'api_keys' || table.name === 'tournament_access' || table.name === 'app_access') {
      return formatTableData(filteredData);
    }
    return [];
  }, [filteredData, table.name]);
  
  const sortedData = useMemo(() => {
    return sortData(tableData, sortConfig);
  }, [tableData, sortConfig]);
  
  // Get current page data
  const startIndex = (pagination.currentPage - 1) * pagination.recordsPerPage;
  const endIndex = startIndex + pagination.recordsPerPage;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Update pagination when filtered data changes
  useEffect(() => {
    if ((table.name === 'organisations' || table.name === 'users' || table.name === 'user_organisations' || table.name === 'api_keys' || table.name === 'tournament_access' || table.name === 'app_access') && filteredData.length >= 0) {
      const totalPages = Math.ceil(filteredData.length / pagination.recordsPerPage);
      setPagination(prev => ({
        ...prev,
        totalPages,
        totalRecords: filteredData.length,
        currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
      }));
    }
  }, [filteredData, table.name, pagination.recordsPerPage]);

  return (
    <>
    <div className="relative h-full flex flex-col">
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : table.name === 'organisations' ? (
        <OrganizationsTable
          searchQuery={searchQuery}
          refreshTrigger={refreshTrigger}
          onEdit={onEdit}
        />
          ) : table.name === 'users' ? (
            <UsersTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'user_organisations' ? (
            <UserOrganisationsTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'api_keys' ? (
            <ApiKeysTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'tournament_access' ? (
            <TournamentAccessTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'app_access' ? (
            <AppAccessTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'tournaments' ? (
            <TournamentsTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'seasons' ? (
            <SeasonsTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'teams' ? (
            <TeamsTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'players' ? (
            <PlayersTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'team_access' ? (
            <TeamAccessTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'player_access' ? (
            <PlayerAccessTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'season_access' ? (
            <SeasonAccessTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : table.name === 'stat_types' ? (
            <StatTypesTable
              searchQuery={searchQuery}
              refreshTrigger={refreshTrigger}
              onEdit={onEdit}
            />
          ) : (
      <div className="space-y-6 flex-1">


        {/* Table Schema Preview */}
        <Accordion title="Table Schema" defaultOpen={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Column Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Constraints
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {columns.map((column, index) => (
                <tr key={column.name}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {column.name}
                    {column.primaryKey && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        PK
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {column.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      {column.nullable && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Nullable
                        </span>
                      )}
                      {column.foreignKey && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          FK
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Accordion>

       {/* Data Table */}
       <Accordion 
         title="Table Data" 
         defaultOpen={true}
        headerContent={
          <div className="flex items-center space-x-4 flex-wrap">
            {/* Active Filter (visible for both tables) */}
            <div className="flex items-center space-x-2">
              <label className="text-xs font-medium text-gray-700">Active:</label>
              <select
                value={filters.active}
                onChange={(e) => setFilters(prev => ({ ...prev, active: e.target.value as 'all' | 'true' | 'false' }))}
                className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>

            {/* Table-specific filters */}
            {table.name === 'organisations' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">API Whitelist:</label>
                <select
                  value={filters.require_api_whitelist}
                  onChange={(e) => setFilters(prev => ({ ...prev, require_api_whitelist: e.target.value as 'all' | 'true' | 'false' }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            )}

            {table.name === 'users' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Role:</label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value as 'all' | string }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}

            {table.name === 'users' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Global Role:</label>
                <select
                  value={filters.global_role}
                  onChange={(e) => setFilters(prev => ({ ...prev, global_role: e.target.value as 'all' | string }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
            )}

            {table.name === 'user_organisations' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Role:</label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value as 'all' | string }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}


            {table.name === 'api_keys' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Rate Limit:</label>
                <select
                  value={filters.rate_limit_per_hour}
                  onChange={(e) => setFilters(prev => ({ ...prev, rate_limit_per_hour: e.target.value as 'all' | 'low' | 'medium' | 'high' }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="low">Low (&lt;100)</option>
                  <option value="medium">Medium (100-999)</option>
                  <option value="high">High (1000+)</option>
                </select>
              </div>
            )}

            {table.name === 'tournament_access' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Access Type:</label>
                <select
                  value={filters.tournament_access_type}
                  onChange={(e) => setFilters(prev => ({ ...prev, tournament_access_type: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="viewer">Viewer</option>
                  <option value="organizer">Organizer</option>
                  <option value="participant">Participant</option>
                </select>
              </div>
            )}

            {table.name === 'app_access' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">App:</label>
                <select
                  value={filters.app_name}
                  onChange={(e) => setFilters(prev => ({ ...prev, app_name: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="replay">Replay</option>
                  <option value="capture">Capture</option>
                  <option value="score">Score</option>
                  <option value="cms">CMS</option>
                  <option value="roster">Roster</option>
                  <option value="reports">Reports</option>
                  <option value="control-room">Control Room</option>
                </select>
              </div>
            )}

            {table.name === 'app_access' && (
              <div className="flex items-center space-x-2">
                <label className="text-xs font-medium text-gray-700">Access:</label>
                <select
                  value={filters.access_level}
                  onChange={(e) => setFilters(prev => ({ ...prev, access_level: e.target.value }))}
                  className="px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="all">All</option>
                  <option value="admin">Admin</option>
                  <option value="edit">Edit</option>
                  <option value="view">View</option>
                </select>
              </div>
            )}

             {/* Created Date Filter */}
             <div className="flex items-center space-x-2 relative">
               <label className="text-xs font-medium text-gray-700">Created:</label>
               <button
                 ref={calendarButtonRef}
                 onClick={(e) => {
                   e.stopPropagation();
                   openCalendar('created');
                 }}
                 className={`px-2 py-1 border border-gray-300 rounded text-xs bg-gray-100 hover:bg-gray-200 cursor-pointer ${
                   filters.createdStartDate && filters.createdEndDate ? 'bg-blue-50 border-blue-300' : ''
                 }`}
                 title={filters.createdStartDate && filters.createdEndDate 
                   ? `${filters.createdStartDate.getDate().toString().padStart(2, '0')}/${(filters.createdStartDate.getMonth() + 1).toString().padStart(2, '0')} - ${filters.createdEndDate.getDate().toString().padStart(2, '0')}/${(filters.createdEndDate.getMonth() + 1).toString().padStart(2, '0')}`
                   : 'Select Created Date Range'
                 }
               >
                 📅 {filters.createdStartDate && filters.createdEndDate ? 'Selected' : 'Select'}
               </button>
               
               {/* Calendar Dropdown positioned relative to this filter */}
               {isCalendarOpen && activeFilterType === 'created' && (
                 <DualCalendarDropdown
                   isOpen={isCalendarOpen}
                   onClose={() => {
                     setIsCalendarOpen(false);
                     setActiveFilterType(null);
                   }}
                   onDateRangeSelect={handleDateRangeSelect}
                   onClearFilter={clearDateFilter}
                   selectedStartDate={filters.createdStartDate || undefined}
                   selectedEndDate={filters.createdEndDate || undefined}
                   triggerRef={calendarButtonRef}
                 />
               )}
             </div>

             {/* Updated Date Filter - Only for tables with updated_at column */}
             {table.name !== 'api_keys' && (
               <div className="flex items-center space-x-2 relative">
                 <label className="text-xs font-medium text-gray-700">Updated:</label>
                 <button
                   ref={updatedCalendarButtonRef}
                   onClick={(e) => {
                     e.stopPropagation();
                     openCalendar('updated');
                   }}
                   className={`px-2 py-1 border border-gray-300 rounded text-xs bg-gray-100 hover:bg-gray-200 cursor-pointer ${
                     filters.updatedStartDate && filters.updatedEndDate ? 'bg-blue-50 border-blue-300' : ''
                   }`}
                   title={filters.updatedStartDate && filters.updatedEndDate 
                     ? `${filters.updatedStartDate.getDate().toString().padStart(2, '0')}/${(filters.updatedStartDate.getMonth() + 1).toString().padStart(2, '0')} - ${filters.updatedEndDate.getDate().toString().padStart(2, '0')}/${(filters.updatedEndDate.getMonth() + 1).toString().padStart(2, '0')}`
                     : 'Select Updated Date Range'
                   }
                 >
                   📅 {filters.updatedStartDate && filters.updatedEndDate ? 'Selected' : 'Select'}
                 </button>
                 
                 {/* Calendar Dropdown positioned relative to this filter */}
                 {isCalendarOpen && activeFilterType === 'updated' && (
                   <DualCalendarDropdown
                     isOpen={isCalendarOpen}
                     onClose={() => {
                       setIsCalendarOpen(false);
                       setActiveFilterType(null);
                     }}
                     onDateRangeSelect={handleDateRangeSelect}
                     onClearFilter={clearDateFilter}
                     selectedStartDate={filters.updatedStartDate || undefined}  
                     selectedEndDate={filters.updatedEndDate || undefined}
                     triggerRef={updatedCalendarButtonRef}
                   />
                 )}
               </div>
             )}

             {/* Expires Date Filter - Only for api_keys table */}
             {table.name === 'api_keys' && (
               <div className="flex items-center space-x-2 relative">
                 <label className="text-xs font-medium text-gray-700">Expires:</label>
                 <button
                   ref={expiresCalendarButtonRef}
                   onClick={(e) => {
                     e.stopPropagation();
                     openCalendar('expires');
                   }}
                   className={`px-2 py-1 border border-gray-300 rounded text-xs bg-gray-100 hover:bg-gray-200 cursor-pointer ${
                     filters.expiresStartDate && filters.expiresEndDate ? 'bg-blue-50 border-blue-300' : ''
                   }`}
                   title={filters.expiresStartDate && filters.expiresEndDate 
                     ? `${filters.expiresStartDate.getDate().toString().padStart(2, '0')}/${(filters.expiresStartDate.getMonth() + 1).toString().padStart(2, '0')} - ${filters.expiresEndDate.getDate().toString().padStart(2, '0')}/${(filters.expiresEndDate.getMonth() + 1).toString().padStart(2, '0')}`
                     : 'Select Expires Date Range'
                   }
                 >
                   📅 {filters.expiresStartDate && filters.expiresEndDate ? 'Selected' : 'Select'}
                 </button>
                 
                 {/* Calendar Dropdown positioned relative to this filter */}
                 {isCalendarOpen && activeFilterType === 'expires' && (
                   <DualCalendarDropdown
                     isOpen={isCalendarOpen}
                     onClose={() => {
                       setIsCalendarOpen(false);
                       setActiveFilterType(null);
                     }}
                     onDateRangeSelect={handleDateRangeSelect}
                     onClearFilter={clearDateFilter}
                     selectedStartDate={filters.expiresStartDate || undefined}  
                     selectedEndDate={filters.expiresEndDate || undefined}
                     triggerRef={expiresCalendarButtonRef}
                   />
                 )}
               </div>
             )}

            {/* Clear All Filters */}
            <button
              onClick={() => setFilters({
                active: 'all',
                require_api_whitelist: 'all',
                role: 'all',
                global_role: 'all',
                rate_limit_per_hour: 'all',
                tournament_access_type: 'all',
                app_name: 'all',
                access_level: 'all',
                createdStartDate: null,
                createdEndDate: null,
                updatedStartDate: null,
                updatedEndDate: null,
                expiresStartDate: null,
                expiresEndDate: null
              })}
              className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
            >
              Clear Filters
            </button>
           </div>
         }
       >
         <TableScrollTopBar>
          {paginatedData.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.name}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group relative"
                      style={{ 
                        width: column.width || 120,
                        minWidth: column.minWidth || 60,
                        maxWidth: column.maxWidth || 300
                      }}
                      onClick={() => handleSort(column.name)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="truncate">{column.name}</span>
                        <div className="flex flex-col ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {sortConfig.key === column.name ? (
                            sortConfig.direction === 'asc' ? (
                              <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )
                          ) : (
                            <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 6a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 14a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((column) => {
                      const cellContent = String((row as any)[column.name] || '-');
                      
                      return (
                        <td
                          key={column.name}
                          className="px-6 py-4 text-sm text-gray-900"
                          style={{ 
                            width: column.width || 120,
                            minWidth: column.minWidth || 60,
                            maxWidth: column.maxWidth || 300
                          }}
                        >
                          <Tooltip content={cellContent} maxLength={15}>
                            <div className="truncate">
                              {cellContent}
                            </div>
                          </Tooltip>
                        </td>
                      );
                    })}
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {/* Edit Icon Button - Disabled for app_access due to RLS issues */}
                          {table.name === 'app_access' ? (
                            <div className="text-gray-300 p-1" title="Edit disabled - RLS restrictions">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </div>
                          ) : (
                            <button 
                              className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                              title="Edit"
                              onClick={() => {
                                // Find the original data from the raw data
                                let originalEntity;
                                if (table.name === 'organisations') {
                                  originalEntity = (data as Organization[]).find((org: Organization) => org.id === row.id);
                                } else if (table.name === 'users') {
                                  originalEntity = (data as User[]).find((user: User) => user.id === row.id);
                                } else if (table.name === 'user_organisations') {
                                  originalEntity = (data as UserOrganisation[]).find((userOrg: UserOrganisation) => userOrg.id === row.id);
                                } else if (table.name === 'api_keys') {
                                  originalEntity = (data as ApiKey[]).find((key: ApiKey) => key.id === row.id);
                                } else if (table.name === 'tournament_access') {
                                  originalEntity = (data as TournamentAccess[]).find((access: TournamentAccess) => access.id === row.id);
                                }
                                if (originalEntity && onEdit) {
                                  onEdit(originalEntity);
                                }
                              }}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                        </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-8 text-center">
              <div className="text-gray-400 mb-2">📋</div>
              <p className="text-gray-500">No data available</p>
            </div>
          )}
        </TableScrollTopBar>
      </Accordion>
      </div>
      )}

          {/* Pagination Component - Only for non-extracted tables */}
          {!isLoading && pagination.totalRecords > 0 && table.name !== 'organisations' && table.name !== 'users' && table.name !== 'user_organisations' && table.name !== 'api_keys' && table.name !== 'tournament_access' && table.name !== 'app_access' && table.name !== 'tournaments' && table.name !== 'seasons' && table.name !== 'teams' && table.name !== 'players' && (
        <div className="bg-white border-t border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left side - Page navigation */}
            <div className="flex items-center space-x-4">
              {/* Previous page button */}
              <button
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ←
              </button>
              
              {/* Page info */}
              <span className="text-sm text-gray-700">Page {pagination.currentPage} of {pagination.totalPages}</span>
              
              {/* Next page button */}
              <button
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage >= pagination.totalPages}
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default TableView;
